import React, { useCallback, useMemo, useState } from 'react';
import { Nav, Page, PageSidebar, NavItem, Modal, Button, Backdrop, Bullseye, NavGroup, PageSidebarBody } from '@patternfly/react-core';
import { ExclamationCircleIcon } from '@patternfly/react-icons'
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { has } from 'lodash-es';

import './Layout.css';

import routes, { doRedirect, Route } from './routes';
import { useSelector } from 'react-redux';
import SplashPage from './pages/splash/SplashPage';
import NewVersionAlert from './components/new-version-alert';
import UserSettings from './pages/user-settings/UserSettings';
import { StoreState } from './store/store';
import { appConfigSelectors } from './store/appConfig/appConfigSlice';
import { configurationTargetSelectors } from './store/configurationTarget/configurationTargetSlice';
import { contouringSelectors } from './store/contouring/contouringSlice';
import { doseSelectors } from './store/dose/doseSlice';
import { imageSelectors } from './store/image/imageSlice';
import { adaptSelectors } from './store/adapt/adaptSlice';
import InteractionRequiredModal from './pages/auth/InteractionRequiredModal';
import CustomMasthead from './Masthead';
import { SupportedModelType } from './store/appConfig/appDeploymentInfoTypes';
import { selectIntersectionOfSupportedModelTypes } from './store/configurationTarget/configurationTargetSelectors';
import AboutPage from './pages/about/AboutPage';

const CLOSE_SIDEBAR_WIDTH_LIMIT = 1200;
const ABOUT_PAGE_SLUG = `/#${routes.aboutPage.slug}`;

const Layout = (props: React.PropsWithChildren) => {

    const { t } = useTranslation();

    const [isSidebarOpen, setSidebarOpen] = useState(true);
    const [isUserSettingsVisible, setUserSettingsVisible] = useState(false);
    const [isHelpDialogVisible, setHelpDialogVisible] = useState(false);
    const isLayoutInsideFrame = useSelector(appConfigSelectors.selectIsLayoutInsideFrame);
    const deploymentInfo = useSelector(appConfigSelectors.selectAppDeploymentInfo);
    const appConfig = useSelector(appConfigSelectors.selectAppConfig);

    const anyUnsavedContourChanges = useSelector(contouringSelectors.selectAnyUnsavedContouringChangesInApp);
    const anyUnsavedDoseChanges = useSelector(doseSelectors.selectAnyUnsavedDoseChangesInApp);
    const anyUnsavedImageChanges = useSelector(imageSelectors.selectAnyUnsavedImageChangesInApp);
    const anyUnsavedAdaptChanges = useSelector(adaptSelectors.selectAnyUnsavedAdaptChangesInApp);

    const handleOpenUserSettings = useCallback(() => {
        setUserSettingsVisible(true);
    }, []);

    const handleCloseUserSettings = useCallback(() => {
        setUserSettingsVisible(false);
    }, []);

    const handleOpenHelpDialog = useCallback(() => {
        // manually set URL to /about (but don't perform an actual redirect)
        doRedirect(ABOUT_PAGE_SLUG, true);

        setHelpDialogVisible(true);
    }, []);

    const handleCloseHelpDialog = useCallback(() => {
        // change history back once exiting the about dialog
        if (window.location.href.indexOf(ABOUT_PAGE_SLUG) !== -1) {
            window.history.back();
        }

        setHelpDialogVisible(false);
    }, []);

    const renderNavItem = useCallback((route: Route) => (
        <NavItem key={route.index} >
            <NavLink exact to={route.slug} activeClassName="pf-m-current" data-testid={route.testId || undefined}>
                {t(route.langKey)}
            </NavLink>
        </NavItem>
    ), [t]);

    const renderNavGroupTitle = useCallback((title: string, unsavedChanges: boolean) => unsavedChanges ? `${title} *` : title, []);

    const isAppInitialized = useSelector((state: StoreState) => state.appStatus.isAppInitialized);
    const isTargetListFetched = useSelector((state: StoreState) => state.configurationTarget.isConfigurationTargetListFetched);
    const isFetchingData = useSelector(configurationTargetSelectors.selectIsFetchingConfigurationTargetData);
    const intersectionOfSupportedModelTypes = useSelector((state: StoreState) => selectIntersectionOfSupportedModelTypes(state))


    const userInitError = useSelector((state: StoreState) =>
        state.configurationTarget.configurationTargetListError || null);
    const errorComponent = userInitError ? (<span className="splash-error" data-testid="configuration-target-list-error"><span className="icon"><ExclamationCircleIcon /></span> {userInitError}</span>) : undefined;


    const isContourConfigSupported = useMemo(() => intersectionOfSupportedModelTypes.includes(SupportedModelType.Contour) || false, [intersectionOfSupportedModelTypes]);
    const isDoseConfigSupported = useMemo(() => intersectionOfSupportedModelTypes.includes(SupportedModelType.Dose) || false, [intersectionOfSupportedModelTypes]);
    const isImageConfigSupported = useMemo(() => intersectionOfSupportedModelTypes.includes(SupportedModelType.Image) || false, [intersectionOfSupportedModelTypes]);
    const isAdaptConfigSupported = useMemo(() => intersectionOfSupportedModelTypes.includes(SupportedModelType.Adapt) || false, [intersectionOfSupportedModelTypes]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handlePageResize = useCallback((_: unknown, object: any) => {
        if (has(object, 'windowSize')) {
            if (object['windowSize'] < CLOSE_SIDEBAR_WIDTH_LIMIT) {
                setSidebarOpen(false);
            } else {
                setSidebarOpen(true);
            }
        }
    }, []);

    if (isLayoutInsideFrame === undefined || !isAppInitialized || !isTargetListFetched || deploymentInfo === undefined || appConfig === undefined || !!userInitError) {
        return <SplashPage loadingText={errorComponent} />;
    }

    const pageNav = (
        <Nav aria-label="Nav">
            {isContourConfigSupported && <NavGroup title={renderNavGroupTitle(t('sideNav.contour'), anyUnsavedContourChanges)} className='nav-group-title'>
                {renderNavItem(routes.contourCustomizationPage)}
                {renderNavItem(routes.contourAllStructuresPage)}
                {renderNavItem(routes.contourModelSelectionPage)}
            </NavGroup>}

            {isDoseConfigSupported && <NavGroup title={renderNavGroupTitle(t('sideNav.dose'), anyUnsavedDoseChanges)} className='nav-group-title'>
                {renderNavItem(routes.doseCustomizationPage)}
                {/* {renderNavItem(routes.doseModelSelectionPage)} */}
            </NavGroup>}

            {isImageConfigSupported && <NavGroup title={renderNavGroupTitle(t('sideNav.image'), anyUnsavedImageChanges)} className='nav-group-title'>
                {renderNavItem(routes.imageCustomizationPage)}
            </NavGroup>}

            {isAdaptConfigSupported && <NavGroup title={renderNavGroupTitle(t('sideNav.adapt'), anyUnsavedAdaptChanges)} className='nav-group-title'>
                {renderNavItem(routes.adaptCustomizationPage)}
            </NavGroup>}

            <NavGroup title={t('sideNav.general')} className='nav-group-title'>
                {appConfig.allowTextEditor && renderNavItem(routes.textEditorPage)}
                {deploymentInfo && !deploymentInfo.disableDaemonConfigPage && renderNavItem(routes.connectorConfigPage)}
                {renderNavItem(routes.licensePage)}
                {renderNavItem(routes.configurationTargetSettingsPage)}
            </NavGroup>
        </Nav>
    );

    const sidebar = (<PageSidebar isSidebarOpen={isSidebarOpen}><PageSidebarBody>{pageNav}</PageSidebarBody></PageSidebar>);
    const masthead = (
        <CustomMasthead
            handleOpenUserSettings={handleOpenUserSettings}
            handleOpenHelpDialog={handleOpenHelpDialog}
            isSidebarOpen={isSidebarOpen}
            setSidebarOpen={setSidebarOpen}
        />
    );



    return (
        <Page header={masthead} sidebar={sidebar} className="main-page" mainContainerId="mvconfigui-page-main" isManagedSidebar={false} onPageResize={handlePageResize}>
            <NewVersionAlert />
            {isFetchingData && (
                <Backdrop data-testid="loading-overlay">
                    <Bullseye>
                        <SplashPage loadingText={errorComponent ? errorComponent : t('splash.loadingConfigurationTarget')} />
                    </Bullseye>
                </Backdrop>
            )}

            {props.children}

            <Modal
                isOpen={isUserSettingsVisible}
                title={t('dialog.settings')}
                variant={'large'}
                position={'top'}
                positionOffset="10%"
                onClose={handleCloseUserSettings}
                actions={[<Button key="close-user-settings" onClick={handleCloseUserSettings} variant='tertiary' data-testid="close-user-settings-modal-button">{t('common.close')}</Button>]}
            >
                <UserSettings />
            </Modal>

            <Modal
                className="about-modal"
                isOpen={isHelpDialogVisible}
                title={t('page.about.title')}
                variant={'large'}
                onClose={handleCloseHelpDialog}
                actions={[<Button key="close-help-dialog" onClick={handleCloseHelpDialog} variant='tertiary'>{t('common.close')}</Button>]}
            >
                <AboutPage isEmbedded />
            </Modal>


            <InteractionRequiredModal />

        </Page>
    );
}

export default Layout;
